// components/pagination.tsx
'use client';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@repo/ui';
import { usePagination } from '@repo/hooks/usePagination';
import { cn } from '../../lib/utils';

interface PaginationProps {
    totalItems: number;
    itemsPerPage: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    siblingCount?: number;
}

export function CustomPagination({
    totalItems,
    itemsPerPage,
    currentPage,
    onPageChange,
    siblingCount = 1,
}: PaginationProps) {
    const { getPageNumbers, totalPages } = usePagination({
        totalItems,
        itemsPerPage,
        currentPage,
        siblingCount,
    });

    if (totalPages <= 1) return null;

    return (
        <Pagination>
            <PaginationContent className="gap-0">
                <PaginationItem>
                    <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1) onPageChange(currentPage - 1);
                        }}
                        aria-disabled={currentPage === 1}
                        className={cn(
                            'text-[#6B7280] disabled:text-[#6B7280] border rounded-e-none rounded-s-[6px] p-[9px] border-[#D1D5DB]',
                            {
                                'opacity-50 cursor-not-allowed': currentPage === 1,
                            },
                        )}
                    />
                </PaginationItem>

                {getPageNumbers().map((page, index) => {
                    if (page === '...') {
                        return (
                            <PaginationItem key={`ellipsis-${index}`}>
                                <PaginationEllipsis className="text-[#6B7280] rounded-none border border-[#D1D5DB] font-inter font-medium text-sm leading-5 tracking-normal text-center" />
                            </PaginationItem>
                        );
                    }

                    return (
                        <PaginationItem key={page}>
                            <PaginationLink
                                className={cn(
                                    'px-4 py-2 text-[#6B7280] rounded-none border border-[#D1D5DB] font-inter font-medium text-sm leading-5 tracking-normal text-center',
                                    currentPage === page &&
                                        'bg-[#EEF2FF] text-primery-blue-500 border-primery-blue-600',
                                )}
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onPageChange(Number(page));
                                }}
                                isActive={currentPage === page}
                            >
                                {page}
                            </PaginationLink>
                        </PaginationItem>
                    );
                })}

                <PaginationItem>
                    <PaginationNext
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages) onPageChange(currentPage + 1);
                        }}
                        aria-disabled={currentPage === totalPages}
                        className={cn(
                            'text-[#6B7280] disabled:text-[#6B7280] border rounded-s-none rounded-e-[6px] p-[9px] border-[#D1D5DB]',
                            {
                                'opacity-50 cursor-not-allowed': currentPage === totalPages,
                            },
                        )}
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
}
